/**
 * Always formats any valid date string as dd/mm/yyyy, regardless of the
 * browser or operating system locale configuration.
 */
export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Same dd/mm/yyyy date renderer but also includes the time component.
 */
export const formatDateTime = (dateString?: string | null): string => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';

  const datePart = formatDate(dateString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${datePart} ${hours}:${minutes}`;
};

/** Return a browser-local calendar date without converting it through UTC. */
export const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Store a levy month as the first calendar day of that month (YYYY-MM-01). */
export const toLevyPeriodDate = (value?: string | null): string => {
  const month = (value || getLocalDateString()).slice(0, 7);
  return /^\d{4}-\d{2}$/.test(month) ? `${month}-01` : `${getLocalDateString().slice(0, 7)}-01`;
};

/** Format a stored levy-period date for an HTML month input. */
export const toLevyPeriodInput = (value?: string | null): string => {
  return (value || getLocalDateString()).slice(0, 7);
};

/**
 * Select the latest meaningful due date from a set of posted statement transactions.
 */
export const getStatementDueDate = (transactions: Array<{ due_date?: string | null }> = []): string | null => {
  const validDates = transactions
    .map((t) => t?.due_date)
    .filter((date): date is string => Boolean(date && date.trim().length > 0))
    .sort();

  if (validDates.length === 0) {
    return null;
  }

  return validDates[validDates.length - 1];
};
